import {
  Component,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AppService } from '@shared/services/app.service';
import { FiltrosReporte } from '@shared/components/filtros-reporte/filtros-reporte';
import { FormField, form, maxLength, min } from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { FormErrorMessagePipe } from '@shared/pipes/form-error-message.pipe';
import {
  UiButton,
  UiDatepicker,
  UiDialog,
  UiInputNumber,
  UiSelect,
  UiTag,
  UiTooltip,
} from '@shared/components/ui';
import { ITablaColumna } from '@core/interfaces/tabla.interface';
import {
  IAnularVentaReq,
  IReporteVentaReq,
  IReporteVentaRes,
} from '@core/interfaces/request-response/venta.dto.interface';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { EAtajoRangoTiempo } from '@core/enums/atajoRangoTiempo.enum';
import { calcularAtajoRangoFecha } from '@shared/utils/functions.utils';
import { EEstadoVenta } from '@core/enums/estadoVenta.enum';
import { IOptionsSelect } from '@core/interfaces/options.select.interface';
import { IEmpleadoData } from '@core/interfaces/entidades/empleado.entidad';
import { EmpleadoApiService } from '@core/services/api/empleado-api.service';
import { ToastService } from '@shared/services/toast.service';
import { ETipoDocumentoSUNAT } from '@core/enums/tipoDocumentoSUNAT.enum';
import { Tabla } from '@shared/components/tabla/tabla';
import { VentaApiService } from '@core/services/api/venta-api.service';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { environment } from '@env/environment';
import { BuscadorCliente } from '@shared/components/buscador-cliente/buscador-cliente';
import { IClienteData } from '@core/interfaces/entidades/cliente.entidad';
import { Paginacion } from '@shared/components/paginacion/paginacion';
import { VerVenta } from '@shared/components/ver-venta/ver-venta';
import { SunatRespuestaPipe } from '../../../shared/pipes/sunat-respuesta.pipe';
import { ModalConfirmacion } from '@shared/components/modal-confirmacion/modal-confirmacion';
import { SucursalService } from '@shared/services/sucursal.service';
import { ISucursalData } from '@core/interfaces/entidades/sucursal.entidad';
import { NombreSucursalPipe } from '../../../shared/pipes/nombre-sucursal.pipe';
import { IReporteVentaDetalleRes } from '@core/interfaces/request-response/venta.dto.interface';
import { EmpresaService } from '@shared/services/empresa.service';
import {
  CreatePrintJobRq,
  ImpresoraDatosVentaRq,
} from '@core/interfaces/request-response/impresora.dto.interface';
import { buildImpresion } from '@shared/utils/formatos_impresion.utils';
import { descargarArchivoDesdeResponse } from '@shared/utils/functions.utils';
import { ETipoImpresion } from '@core/enums/impresora.enum';
import { ImpresoraService } from '@shared/services/impresora.service';
import { ImpresoraApiService } from '@core/services/api/impresora-api.service';
import { ID_DESCARGA_PDF } from '@shared/constants/impresora.const';
import { QzService } from '@shared/services/qz.service';
import { obtenerFooterMensajePorDocumento } from '@shared/utils/formatos_impresion.utils';

@Component({
  selector: 'app-reporte-venta',
  imports: [
    FiltrosReporte,
    FormsModule,
    UiDatepicker,
    UiButton,
    UiDialog,
    UiInputNumber,
    UiSelect,
    UiTag,
    UiTooltip,
    FormField,
    FormErrorMessagePipe,
    Tabla,
    BuscadorCliente,
    Paginacion,
    DatePipe,
    CurrencyPipe,
    VerVenta,
    SunatRespuestaPipe,
    ModalConfirmacion,
    NombreSucursalPipe,
  ],
  templateUrl: './reporte-venta.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reporte-venta.css',
})
export class ReporteVenta implements OnInit {
  constructor() {
    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.cargarOpcionesSelect();
        this.cargarEmpleados();
        this.cargarOpcionesSucursal();
      }
    });
  }
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Reporte de Venta',
      subtitle: 'Visualiza el reporte de venta',
      showBackButton: true,
      icon: 'bi bi-receipt',
    });
    this.columnasTabla = [
      { key: 'id', titulo: 'Caja' },
      { key: 'sucursal_id', titulo: 'Sucursal', template: this.sucursalColumnTemplate() },
      { key: 'usuario_nombre_completo', titulo: 'Empleado' },
      { key: 'cliente_nombre_completo', titulo: 'Cliente' },
      { key: 'total', titulo: 'Total', pipe: new CurrencyPipe(environment.locale, 'PEN') },
      { key: 'descuento', titulo: 'Descuento', pipe: new CurrencyPipe(environment.locale, 'PEN') },
      { key: 'estado', titulo: 'Estado', template: this.columnaEstadoTemplate() },
      { key: 'serie_documento', titulo: 'Documento' },
      { key: 'correlativo_documento', titulo: 'Número' },
      { key: 'tipo_documento_sunat', titulo: 'Tipo Documento' },
    ];
  }

  // servicios
  private appService = inject(AppService);
  private toastService = inject(ToastService);
  private empleadoApiService = inject(EmpleadoApiService);
  private ventaApiService = inject(VentaApiService);
  private sucursalService = inject(SucursalService);
  canSelectSucursal = this.sucursalService.canSelectSucursal;
  private empresaService = inject(EmpresaService);
  private impresoraService = inject(ImpresoraService);
  private impresoraApiService = inject(ImpresoraApiService);
  private qzService = inject(QzService);
  // datos
  private columnaEstadoTemplate = viewChild('columnaEstadoTemplate');
  private itemsPorPagina = 18;
  private requestFiltro: IReporteVentaReq | null = null;

  EEstadoVenta = EEstadoVenta;
  maxDate = new Date(new Date().setHours(23, 59, 59, 999));
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  columnasTabla: ITablaColumna[] = [];
  paginacion = signal<IPaginar<IReporteVentaRes> | null>(null);
  opcionesEstado = signal<IOptionsSelect<EEstadoVenta | null>[]>([]);
  opcionesTipoDocumentoSunat = signal<IOptionsSelect<ETipoDocumentoSUNAT | null>[]>([]);
  modal_ver_venta = signal<boolean>(false);
  ver_detalles_venta = signal<boolean>(false);
  reporte_venta_seleccionada = signal<IReporteVentaRes | null>(null);
  modal_confirmar_anular_venta = signal<boolean>(false);
  anulandoVenta = signal<boolean>(false);
  reemitiendoVenta = signal<boolean>(false);
  descargandoPdf = signal<boolean>(false);
  // formulario
  formFiltro = form(
    signal({
      rangoFecha: null as Date[] | null,
      estado: null as EEstadoVenta | null,
      empleado: null as IEmpleadoData | null,
      tipo_documento_sunat: null as ETipoDocumentoSUNAT | null,
      cliente: null as IClienteData | null,
      serie_documento: '' as string,
      numero_documento: 0 as number,
      sucursal: null as ISucursalData | null,
    }),
    (x) => {
      maxLength(x.serie_documento, 10, { message: 'La serie no puede tener más de 10 caracteres' });
      min(x.numero_documento, 0, { message: 'El número de documento no puede ser negativo' });
    },
  );

  opcionesEmpleado = signal<IOptionsSelect<IEmpleadoData | null>[]>([]);
  opcionesSucursal = signal<IOptionsSelect<ISucursalData | null>[]>([]);

  // métodos
  cambiarPagina(pagina: number) {
    if (this.requestFiltro === null) return;
    this.ventaApiService.paginarReporte(this.requestFiltro, pagina, this.itemsPorPagina).subscribe({
      next: (rs) => {
        this.paginacion.set({
          ...rs,
        });
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'No se pudo cargar el reporte de ventas',
        );
      },
    });
  }
  EAtajoRangoTiempo = EAtajoRangoTiempo;
  atajoRangoFecha(tiempo: EAtajoRangoTiempo) {
    const { inicio, fin } = calcularAtajoRangoFecha(tiempo);
    this.formFiltro.rangoFecha().value.set([inicio, fin]);
  }
  limpiarFiltros() {
    this.formFiltro.rangoFecha().value.set(null);
    this.formFiltro.estado().value.set(null);
    this.formFiltro.empleado().value.set(null);
    this.formFiltro.tipo_documento_sunat().value.set(null);
    this.formFiltro.serie_documento().value.set('');
    this.formFiltro.numero_documento().value.set(0);
    this.formFiltro.cliente().value.set(null);
    if (this.canSelectSucursal()) {
      this.formFiltro.sucursal().value.set(null);
    }
  }
  onModalVentaClose() {
    this.modal_ver_venta.set(false);
    this.reporte_venta_seleccionada.set(null);
    this.ver_detalles_venta.set(false);
  }
  colorEstadoTemplate(
    estado: EEstadoVenta,
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
    switch (estado) {
      case EEstadoVenta.PENDIENTE:
      case EEstadoVenta.SUNAT_PENDIENTE_ANULACION:
        return 'warning';
      case EEstadoVenta.PENDIENTE_EMISION:
        return 'info';
      case EEstadoVenta.SUNAT_PENDIENTE:
        return 'contrast';
      case EEstadoVenta.SUNAT_RECHAZADO:
        return 'danger';
      case EEstadoVenta.ANULADO:
      case EEstadoVenta.FINALIZADO:
        return 'success';
      default:
        return 'secondary';
    }
  }
  private ver_detalles_venta_component = viewChild<VerVenta>('verVentaComponent');
  verDetalleVenta() {
    if (this.ver_detalles_venta() == false && this.reporte_venta_seleccionada() != null) {
      this.ver_detalles_venta_component()?.mostrarDetalles(this.reporte_venta_seleccionada()!.id);
      this.ver_detalles_venta.set(true);
    }
  }
  // botones de acciones
  verReporteVenta(venta: IReporteVentaRes) {
    this.modal_ver_venta.set(true);
    this.reporte_venta_seleccionada.set(venta);
  }
  reemitirVenta(venta: IReporteVentaRes) {
    if (this.reemitiendoVenta()) return;
    this.reemitiendoVenta.set(true);
    this.ventaApiService.reemitirVenta(venta.id).subscribe({
      next: (rs) => {
        this.toastService.show('top-right', 'success', 'Éxito', 'Venta reemitida correctamente.');
        this.reemitiendoVenta.set(false);
        this.cambiarPagina(1);
      },
      error: (err) => {
        this.reemitiendoVenta.set(false);
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'No se pudo reemitir la venta.',
        );
      },
    });
  }

  descargarOImprimirPdf(venta: IReporteVentaRes) {
    if (this.descargandoPdf()) return;
    const idImpresora = this.impresoraService.getImpresoraSeleccionada();

    if (idImpresora === null || idImpresora === undefined) {
      return;
    }

    const empresaData = this.empresaService.getEmpresaData();
    if (!empresaData) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'No se pudo obtener la información de la empresa.',
      );
      return;
    }

    this.descargandoPdf.set(true);
    this.ventaApiService.cargarVenta(venta.id).subscribe({
      next: async (ventaDetalle: IReporteVentaDetalleRes) => {
        const ventaParaImpresion: ImpresoraDatosVentaRq = {
          documento_sunat: {
            tipo_documento_sunat: venta.tipo_documento_sunat,
            serie_documento: venta.serie_documento,
            numero_documento: venta.correlativo_documento,
          },
          cliente: {
            nombre_completo: venta.cliente_nombre_completo || 'CLIENTE VARIOS',
            dniRuc: venta.cliente_dni?.toString() || '99999999',
          },
          productos_comprados: ventaDetalle.detalle.map((item) => ({
            id: 0,
            nombre: item.producto_nombre,
            cantidad: item.cantidad,
            precio_unitario: item.precio_unitario,
            codigo: item.producto_codigo,
            descuento_valor_aplicado: (item as any).descuento_valor_aplicado ?? 0,
          })),
          igv_porcentaje: venta.igv_porcentaje,
          total: venta.total,
          descuento: venta.descuento,
          fecha_venta: venta.created_at,
        };

        const filename = `VentaClick_${ventaParaImpresion.documento_sunat.serie_documento}${ventaParaImpresion.documento_sunat.numero_documento.toString().padStart(8, '0')}`;

        if (idImpresora === ID_DESCARGA_PDF) {
          const sucursal = this.sucursalService.sucursalPorID(venta.sucursal_id) ?? null;
          const footerMensaje = obtenerFooterMensajePorDocumento(
            sucursal,
            venta.tipo_documento_sunat,
          );
          const htmlContent = await buildImpresion(
            empresaData,
            ETipoImpresion.VENTA,
            ventaParaImpresion,
            footerMensaje,
          );
          await this.qzService.printHtmlWithBrowserDialog(htmlContent, filename, 80);
          return;
        }

        const rq: CreatePrintJobRq = {
          tipo_impresion: ETipoImpresion.VENTA,
          id_config_impresora: idImpresora,
          datos: ventaParaImpresion,
        };
        this.impresoraApiService.imprimir(rq).subscribe({
          next: (res) => {
            if (!res.success) {
              this.descargandoPdf.set(false);
              this.toastService.show(
                'top-right',
                'error',
                'Error',
                res.message || 'No se pudo enviar la venta a imprimir.',
              );
              return;
            }
            this.descargandoPdf.set(false);
            this.toastService.show(
              'top-right',
              'success',
              'Impresión enviada',
              'La venta se ha enviado a imprimir correctamente.',
            );
          },
          error: (err) => {
            this.descargandoPdf.set(false);
            console.error('Error al imprimir:', err);
            if (err.status === 401) return;
            this.toastService.show(
              'top-right',
              'error',
              'Error',
              err.error?.message || 'No se pudo enviar la venta a imprimir.',
            );
          },
        });
      },
      error: (err) => {
        this.descargandoPdf.set(false);
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'No se pudo generar el PDF de la venta.',
        );
      },
    });
  }

  anularVenta(venta: IReporteVentaRes) {
    this.reporte_venta_seleccionada.set(venta);
    this.modal_confirmar_anular_venta.set(true);
  }
  confirmarAnularVenta(motivo?: string) {
    if (this.reporte_venta_seleccionada() == null) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'No se ha seleccionado ninguna venta para anular.',
      );
      return;
    }
    if (this.anulandoVenta()) return;
    this.anulandoVenta.set(true);
    const rq: IAnularVentaReq = {
      venta_id: this.reporte_venta_seleccionada()!.id,
      motivo: motivo || 'Anulación desde el reporte de ventas',
    };
    this.ventaApiService.anularVenta(rq).subscribe({
      next: (rs) => {
        this.anulandoVenta.set(false);
        this.toastService.show('top-right', 'success', 'Éxito', 'Venta anulada correctamente.');
        this.modal_confirmar_anular_venta.set(false);
        this.reporte_venta_seleccionada.set(null);
        this.cambiarPagina(1);
      },
      error: (err) => {
        this.anulandoVenta.set(false);
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'No se pudo anular la venta.',
        );
      },
    });
  }
  // formulario y validaciones
  private checkInvalidForm() {
    if (
      this.formFiltro().invalid() ||
      (this.formFiltro.rangoFecha().value() === null &&
        this.formFiltro.numero_documento().value() === 0 &&
        this.formFiltro.serie_documento().value().trim().length === 0)
    ) {
      this.formFiltro.rangoFecha().markAsTouched();
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'Si no hay filtros de fecha, debe ingresar al menos numero de serie o documento.',
      );
      return true;
    }
    return false;
  }
  aplicarFiltros(event: Event) {
    event.preventDefault();
    if (this.checkInvalidForm()) return;
    const req = this.buildRequestFiltro();
    if (!req) return;

    this.requestFiltro = req;
    this.cambiarPagina(1);
  }

  exportarExcel() {
    if (this.checkInvalidForm()) return;
    const req = this.buildRequestFiltro();
    if (!req) return;

    this.ventaApiService.exportarReporteExcel(req).subscribe({
      next: (response) => {
        descargarArchivoDesdeResponse(response, 'reporte-ventas.xlsx');
        this.toastService.show('top-right', 'success', 'Excel', 'Reporte de ventas descargado.');
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error?.message || 'No se pudo exportar el reporte de ventas.',
        );
      },
    });
  }

  private buildRequestFiltro(): IReporteVentaReq | null {
    const {
      rangoFecha,
      estado,
      empleado,
      tipo_documento_sunat,
      cliente,
      serie_documento,
      numero_documento,
      sucursal,
    } = this.formFiltro().controlValue();

    return {
      fecha_inicio: rangoFecha ? rangoFecha[0] : undefined,
      fecha_fin: rangoFecha ? rangoFecha[1] : undefined,
      sucursal_id: sucursal ? sucursal.id : undefined,
      estado: estado ?? undefined,
      empleado_id: empleado ? empleado.id : undefined,
      tipo_documento_sunat: tipo_documento_sunat ?? undefined,
      cliente_id: cliente ? cliente.id : undefined,
      serie_documento: serie_documento.trim().length > 0 ? serie_documento : undefined,
      numero_documento: numero_documento !== 0 ? numero_documento : undefined,
    };
  }

  private cargarEmpleados() {
    this.empleadoApiService.cargar().subscribe({
      next: (rs) => {
        this.opcionesEmpleado.set(
          rs.map((empleado) => ({
            label: empleado.nombre_completo,
            value: empleado,
          })),
        );
        // agregamos opción de todos
        this.opcionesEmpleado.update((options) => [
          {
            label: 'Todos los empleados',
            value: null,
          },
          ...options,
        ]);
      },
      error: (err) => {
        if (err.status == 401) return;
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err.error.message || 'No se pudieron cargar los empleados',
        );
      },
    });
  }
  private cargarOpcionesSucursal() {
    const sucursales = this.sucursalService.sucursales();
    const restringidaId = this.sucursalService.sucursalRestringidaId();

    if (restringidaId != null) {
      const restringida = sucursales.find((s) => s.id === restringidaId);
      this.opcionesSucursal.set(
        restringida ? [{ label: restringida.nombre, value: restringida }] : [],
      );
      this.formFiltro.sucursal().value.set(restringida ?? null);
      return;
    }

    this.opcionesSucursal.set(
      sucursales.map((sucursal) => ({
        label: sucursal.nombre,
        value: sucursal,
      })),
    );
    this.opcionesSucursal.update((options) => [
      {
        label: 'Todas las sucursales',
        value: null,
      },
      ...options,
    ]);
  }
  private cargarOpcionesSelect() {
    this.opcionesEstado.set(
      Object.values(EEstadoVenta).map((estado) => ({
        label: estado.replaceAll('_', ' '),
        value: estado,
      })),
    );
    this.opcionesEstado.update((options) => [
      {
        label: 'Todos',
        value: null,
      },
      ...options,
    ]);
    this.opcionesTipoDocumentoSunat.set(
      Object.values(ETipoDocumentoSUNAT).map((tipo) => ({
        label: tipo.replaceAll('_', ' '),
        value: tipo,
      })),
    );
    this.opcionesTipoDocumentoSunat.update((options) => [
      {
        label: 'Todos',
        value: null,
      },
      ...options,
    ]);
  }
  private sucursalColumnTemplate = viewChild('sucursalColumnTemplate');
}
